import type { APIState, Result } from '@api';
import { Failure, Success, useFMSAPI, useErrorMessage } from '@api';
import { projectAtom, SCOPES, useHasScope } from '@data/auth';
import { environmentAtom } from '@data/fms/environment/states';
import type { VehicleCalibrationParameterDetail } from '@data/fms/vehicle/types';
import { useNotification } from '@data/notification';
import type { AxiosResponse, AxiosError } from 'axios';
import { useAtomCallback } from 'jotai/utils';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * キャリブレーションパラメータ詳細取得API
 */
export const useGetCalibrationParameterDetailAPI = (): {
  state: APIState;
  getCalibrationParameterDetail: (
    vehicle_id: string,
    version_id: string,
    lang: string,
  ) => Promise<VehicleCalibrationParameterDetail | null>;
} => {
  const [state, setState] = useState<APIState>('none');
  const { notifyError } = useNotification();
  const fmsAPI = useFMSAPI();
  const getHasScope = useHasScope();
  const getErrorMessage = useErrorMessage();
  const { t } = useTranslation();

  const getCalibrationParameterDetailAPI = useCallback(
    async (
      projectId: string,
      environmentId: string,
      vehicleId: string,
      versionId: string,
      lang: string,
    ): Promise<Result<VehicleCalibrationParameterDetail, AxiosResponse>> => {
      try {
        const res = await fmsAPI.get(
          `/${projectId}/environments/${environmentId}/vehicles/${vehicleId}/calibration_parameters/${versionId}/details?lang=${lang}`,
        );
        return new Success(res?.data);
      } catch (error) {
        return new Failure((error as AxiosError).response as AxiosResponse);
      }
    },
    [fmsAPI],
  );

  const getCalibrationParameterDetail = useAtomCallback(
    useCallback(
      async (get, _, vehicleId: string, versionId: string, lang: string) => {
        const project = get(projectAtom);
        const environment = get(environmentAtom);
        if (!project || !environment) {
          notifyError({
            message: t('api.fms.get_calibration_parameter_detail', {
              status: 'failed',
            }),
          });
          setState('hasError');
          return null;
        }
        setState('loading');
        if (!getHasScope(SCOPES.DescribeCalibrationParameterEditConfigs)) {
          setState('hasError');
          return null;
        }
        const res = await getCalibrationParameterDetailAPI(
          project!.id,
          environment!.environment_id,
          vehicleId,
          versionId,
          lang,
        );
        if (!res || res.isFailure()) {
          notifyError({
            message: `${t('api.fms.get_calibration_parameter_detail', {
              status: 'failed',
            })}: ${getErrorMessage(res)}`,
          });
          setState('hasError');
          return null;
        }
        setState('hasValue');
        return res.value;
      },
      [
        notifyError,
        getCalibrationParameterDetailAPI,
        getHasScope,
        t,
        getErrorMessage,
      ],
    ),
  );

  return {
    state,
    getCalibrationParameterDetail,
  };
};
